import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { ConfigCacheService } from '@services/api/config-cache.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { defaultUserPreferences, ProjectSetupNavState, UserPreferences } from './user-preference.models';

/**
 * UserPreferenceService — display preferences for the Project & Lead pages.
 *
 * Backed by the real API:
 *   - `getPreferences()`  → GET  /users/display-preferences/:userId
 *   - `savePreferences()` → POST /users/display-preferences
 *
 * `localStorage` is kept ONLY as a write-through cache so the Project/Lead pages
 * get an instant, flash-free first paint via `getCachedPreferences()` before the
 * async GET resolves; the API is always the source of truth. If the API errors,
 * we fall back to that cache (or defaults) so the UI never breaks.
 *
 * `preferences$` lets any screen react to live changes without re-fetching.
 *
 * See the sibling `user-preferences-backend-requirements.md` for the endpoint spec.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferenceService {
  private readonly STORAGE_PREFIX = 'remmi_display_preferences_';

  private readonly endpoint = '/users/display-preferences';

  /** Last known preferences, broadcast to subscribers (Project/Lead pages). */
  private readonly _preferences$ = new BehaviorSubject<UserPreferences | null>(null);
  readonly preferences$ = this._preferences$.asObservable();

  constructor(
    private api: ApiService,
    private configCache: ConfigCacheService
  ) {}

  private storageKey(userId: string): string {
    return `${this.STORAGE_PREFIX}${userId || 'anon'}`;
  }

  /**
   * Merge a (possibly partial / null) saved object over defaults so newly-added
   * keys are never undefined — the backend may omit keys it doesn't know yet.
   */
  private mergeWithDefaults(userId: string, saved: Partial<UserPreferences> | null | undefined): UserPreferences {
    const base = defaultUserPreferences(userId);
    if (!saved) return base;
    return {
      ...base,
      ...saved,
      user_id: userId || saved.user_id || base.user_id,
      project_page: { ...base.project_page, ...(saved.project_page || {}) },
      lead_page: {
        ...base.lead_page,
        ...(saved.lead_page || {}),
        filterFields: { ...base.lead_page.filterFields, ...(saved.lead_page?.filterFields || {}) },
        defaultFilters: { ...base.lead_page.defaultFilters, ...(saved.lead_page?.defaultFilters || {}) },
        followUpSort: { ...base.lead_page.followUpSort, ...(saved.lead_page?.followUpSort || {}) },
        // A list, not a keyed object — take what was saved, and never let a null reach the
        // Lead page's `.filter()` on it.
        visibleInactiveStatuses: saved.lead_page?.visibleInactiveStatuses ?? base.lead_page.visibleInactiveStatuses,
      },
      dashboard_page: {
        ...base.dashboard_page,
        ...(saved.dashboard_page || {}),
        // layout is a list, not a keyed object — take saved as-is when present.
        layout: saved.dashboard_page?.layout ?? base.dashboard_page.layout,
      },
      dashboard_new_page: {
        ...base.dashboard_new_page,
        ...(saved.dashboard_new_page || {}),
        layout: saved.dashboard_new_page?.layout ?? base.dashboard_new_page.layout,
      },
      project_list_page: {
        ...base.project_list_page,
        ...(saved.project_list_page || {}),
        filterFields: { ...base.project_list_page.filterFields, ...(saved.project_list_page?.filterFields || {}) },
      },
      project_leads_page: {
        ...base.project_leads_page,
        ...(saved.project_leads_page || {}),
        filterFields: { ...base.project_leads_page.filterFields, ...(saved.project_leads_page?.filterFields || {}) },
        followUpSort: { ...base.project_leads_page.followUpSort, ...(saved.project_leads_page?.followUpSort || {}) },
      },
      properties_page: {
        ...base.properties_page,
        ...(saved.properties_page || {}),
        filterFields: { ...base.properties_page.filterFields, ...(saved.properties_page?.filterFields || {}) },
      },
      listings_page: {
        ...base.listings_page,
        ...(saved.listings_page || {}),
        filterFields: { ...base.listings_page.filterFields, ...(saved.listings_page?.filterFields || {}) },
      },
      contract_list_page: {
        ...base.contract_list_page,
        ...(saved.contract_list_page || {}),
        filterFields: { ...base.contract_list_page.filterFields, ...(saved.contract_list_page?.filterFields || {}) },
      },
      chat: {
        ...base.chat,
        ...(saved.chat || {}),
        // Lists, not keyed objects — take what was saved, and never let a null through to a
        // `.includes()` on the chat sidebar.
        favouritePeople: saved.chat?.favouritePeople ?? base.chat.favouritePeople,
        favouriteGroups: saved.chat?.favouriteGroups ?? base.chat.favouriteGroups,
      },
      // Keyed by project id — a document saved before this key existed reads back as `{}`.
      project_setup_nav: { ...base.project_setup_nav, ...(saved.project_setup_nav || {}) },
    };
  }

  /** Write-through cache for flash-free first paint on next page load. */
  private cacheLocally(userId: string, prefs: UserPreferences): void {
    try {
      localStorage.setItem(this.storageKey(userId), JSON.stringify(prefs));
    } catch {
      // Storage full/blocked — cache is best-effort only.
    }
  }

  private readLocalCache(userId: string): UserPreferences | null {
    try {
      const raw = localStorage.getItem(this.storageKey(userId));
      if (raw) {
        return this.mergeWithDefaults(userId, JSON.parse(raw) as UserPreferences);
      }
    } catch {
      // Corrupt cache → ignore.
    }
    return null;
  }

  /**
   * GET /users/display-preferences/:userId
   * Returns the user's saved preferences (or defaults if none / on error).
   */
  getPreferences(): Observable<UserPreferences> {
    const userId = this.api.getUserId();

    return this.api.get(`${this.endpoint}/${userId}`).pipe(
      map((res: any) => this.mergeWithDefaults(userId, res?.data)),
      tap(prefs => {
        this._preferences$.next(prefs);
        this.cacheLocally(userId, prefs);
      }),
      catchError(() => {
        // API unreachable → use the last good cache, else defaults.
        const fallback = this.readLocalCache(userId) ?? defaultUserPreferences(userId);
        this._preferences$.next(fallback);
        return of(fallback);
      })
    );
  }

  /**
   * GET /users/display-preferences/:userId for someone other than the logged-in user — read-only
   * (master admin "view dashboard as"), so it never touches the broadcast stream or the local
   * cache, both of which belong to the logged-in user.
   */
  getPreferencesForUser(userId: string): Observable<UserPreferences> {
    return this.api.get(`${this.endpoint}/${userId}`).pipe(
      map((res: any) => this.mergeWithDefaults(userId, res?.data)),
      catchError(() => of(defaultUserPreferences(userId)))
    );
  }

  /**
   * POST /users/display-preferences
   * Upserts the current user's preferences and returns the stored record.
   */
  savePreferences(prefs: UserPreferences): Observable<{ status: boolean; data: UserPreferences }> {
    const userId = this.api.getUserId();
    const payload: UserPreferences = { ...prefs, user_id: userId };

    return this.api.post(this.endpoint, payload).pipe(
      map((res: any) => ({
        status: res?.status ?? true,
        // Prefer the server's echoed record (has updated_at); fall back to payload.
        data: this.mergeWithDefaults(userId, res?.data ?? payload),
      })),
      tap(result => {
        this._preferences$.next(result.data);
        this.cacheLocally(userId, result.data);
        // The session GET cache (ConfigCacheInterceptor) would otherwise keep serving the
        // pre-save copy to any screen that calls getPreferences() again this session.
        this.configCache.evict('/users/display-preferences');
      })
    );
  }

  /**
   * Synchronous best-effort read for pages that need an immediate value on init
   * (before the async `getPreferences()` resolves). Uses the in-memory value, the
   * local write-through cache, then defaults.
   */
  getCachedPreferences(): UserPreferences {
    const cached = this._preferences$.value;
    if (cached) return cached;

    const userId = this.api.getUserId();
    return this.readLocalCache(userId) ?? defaultUserPreferences(userId);
  }

  /** Project Set Up side-menu state for one project; an unknown project means an open menu. */
  getProjectNavState(projectId: string): ProjectSetupNavState {
    return this.getCachedPreferences().project_setup_nav?.[projectId] ?? { collapsed: false };
  }

  /**
   * Saves the side-menu state for one project.
   *
   * The whole preferences document goes up (that is what the endpoint takes), but the API merges
   * `project_setup_nav` per project id, so two tabs on two projects cannot clobber each other.
   */
  saveProjectNavState(projectId: string, state: ProjectSetupNavState): Observable<{ status: boolean; data: UserPreferences }> {
    const prefs = this.getCachedPreferences();

    return this.savePreferences({
      ...prefs,
      project_setup_nav: {
        ...prefs.project_setup_nav,
        [projectId]: { ...prefs.project_setup_nav?.[projectId], ...state },
      },
    });
  }
}
